import { useState, useEffect, useCallback } from 'react';

import { AuthContext, type UserInfo } from '../hooks/useAuth';
import { apiRequest, AUTH_EXPIRED_EVENT } from '@/src/services';
import { Constants } from '@/src/utils';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const res = await apiRequest<UserInfo>('get', 'getUserDetails');
                const newUser = res.data;

                // SECURITY GUARD: Frontend Check (If Middleware allowed read)
                if (newUser.role === Constants.ROLES.STUDENT) {
                    redirectToClassroom();
                    return;
                }

                newUser.allowedGrades = getAllowedGrades(newUser.gradeShortCode);

                setUser(newUser);
            } catch (err: any) {
                // SECURITY GUARD: Backend Rejection Handler
                // If the backend returned 403, it means they are logged in but unauthorized (Student)
                // We redirect them to Classroom to break the infinite login loop.
                if (err?.response?.status === 403) {
                    redirectToClassroom();
                    return;
                }

                // Standard Error (401, Network, etc.) -> Treat as logged out
                setUser(null);
            } finally {
                setLoading(false);
            }
        };
        checkAuth();
    }, []);

    const redirectToClassroom = () => {
        const isLocal = window.location.hostname === 'localhost';
        const classroomDashboard = isLocal
            ? 'http://localhost:5173'
            : 'https://classroom.paradigmmath.com';

        window.location.href = classroomDashboard;
    };

    const login = (userData: UserInfo) => {
        userData.allowedGrades = getAllowedGrades(userData.gradeShortCode);
        setUser(userData);
    };

    const logout = useCallback(async () => {
        try {
            await apiRequest('post', 'auth/logout');
        } catch (err) {
            console.error('Logout API call failed', err);
        } finally {
            setUser(null);

            const isLocal = window.location.hostname === 'localhost';

            const classroomBaseUrl = isLocal
                ? 'http://localhost:5173'
                : 'https://classroom.paradigmmath.com';

            window.location.href = `${classroomBaseUrl}/logout`;
        }
    }, []);

    const addMyLesson = useCallback(
        async (lessonId: string) => {
            if (!user) return;

            const updatedUser = { ...user };
            if (!updatedUser.myLessons?.includes(lessonId)) {
                updatedUser.myLessons.push(lessonId);
                try {
                    setUser(updatedUser);
                    await apiRequest('POST', 'user/myLessons', {
                        myLessons: updatedUser.myLessons,
                    });
                } catch {
                    setUser(user);
                }
            }
        },
        [user]
    );
    const removeMyLesson = useCallback(
        async (lessonId: string) => {
            if (!user) return;

            const updatedUser = { ...user };
            if (updatedUser.myLessons?.includes(lessonId)) {
                updatedUser.myLessons = updatedUser.myLessons.filter((id) => id !== lessonId);
                try {
                    setUser(updatedUser);
                    await apiRequest('POST', 'user/myLessons', {
                        myLessons: updatedUser.myLessons,
                    });
                } catch {
                    setUser(user);
                }
            }
        },
        [user]
    );

    useEffect(() => {
        const handleAuthExpired = () => {
            setUser(null);
        };
        window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
        return () => {
            window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
        };
    }, [logout]);

    return (
        <AuthContext.Provider value={{ user, login, logout, loading, addMyLesson, removeMyLesson }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

function getAllowedGrades(gradeShortCode: string): {
    elementary: string[];
    middleSchool: string[];
} {
    const elementaryGrades = ['TK', 'K', '1', '2', '3', '4', '5', '6'];
    const middleSchoolGrades = ['6M', '7', '8'];

    let allowedElementary: string[] = [];
    let allowedMiddleSchool: string[] = [];

    if (gradeShortCode === 'A') {
        allowedElementary = [...elementaryGrades];
        allowedMiddleSchool = [...middleSchoolGrades];
    } else if (elementaryGrades.includes(gradeShortCode)) {
        allowedElementary = [...elementaryGrades];
    } else if (middleSchoolGrades.includes(gradeShortCode)) {
        allowedElementary = ['5'];
        allowedMiddleSchool = [...middleSchoolGrades];
    }

    return {
        elementary: allowedElementary,
        middleSchool: allowedMiddleSchool,
    };
}
